import type { AxiosInstance } from 'axios';
import { createCrudService, type CrudService } from '../core/crud';
import { unwrapItems, type ObjectResponse } from '../core/envelope';
import type {
  StorefrontMenu,
  StorefrontMenuCreate,
  StorefrontMenuFilter,
} from '../data/storefrontMenu';

/**
 * Admin CRUD for storefront navigation, plus `tree()` which returns the nested
 * structure INCLUDING disabled rows so the panel can still show and re-enable
 * something that was switched off.
 *
 * Storefront reads use the anonymous `publicCatalogService.getNav()` instead.
 * Delete cascades server-side: removing a menu removes its columns and links
 * rather than stranding them.
 */
export function createStorefrontMenuService(client: AxiosInstance) {
  const crud = createCrudService<
    StorefrontMenu,
    StorefrontMenuCreate,
    StorefrontMenuFilter
  >(client, '/StorefrontMenu', 'storefrontMenuId');

  return {
    ...crud,
    tree: async (placement = 'header'): Promise<StorefrontMenu[]> => {
      const res = await client.post<ObjectResponse<StorefrontMenu[]>>(
        '/StorefrontMenu/tree',
        {},
        { params: { placement } },
      );
      return unwrapItems(res.data);
    },
  };
}

export type StorefrontMenuService = CrudService<
  StorefrontMenu,
  StorefrontMenuCreate,
  StorefrontMenuFilter
> & {
  tree: (placement?: string) => Promise<StorefrontMenu[]>;
};
